import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FormCard, Button, Add, theme } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import LinkConsentModal from './LinkConsentModal'
import ConsentFormsList from './ConsentFormsList'
import { ConsentFormData } from './ConsentFormItem'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import { useConsentForms, ConsentForm } from '../../../../hooks/useConsentForms'

interface ConsentSectionProps {
  residentId: string
  consentForms?: ConsentFormData[]
  value?: string[]
  disabled?: boolean
  onChange?: (consentIds: string[]) => void
}

const ConsentSection: React.FC<ConsentSectionProps> = ({
  residentId,
  consentForms,
  value = [],
  disabled = false,
  onChange
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const { data: allConsentForms } = useConsentForms({
    residentId,
    enabled: !!residentId && !disabled
  })

  const displayedForms =
    consentForms ||
    (allConsentForms ?? []).filter((form: ConsentForm) =>
      value.includes(form.id)
    )

  const handleOpenModal = () => {
    setIsModalVisible(true)
  }

  const handleLinkConsents = (selectedIds: string[]) => {
    onChange?.(selectedIds)
  }

  const hasConsentForms = displayedForms.length > 0

  return (
    <>
      <FormCard
        title={
          <TitleContainer>
            <FormCardHeading level={3}>
              {t(carePlansConstants.CONSENT_SECTION_TITLE)}
            </FormCardHeading>
            <DescriptionText>
              {t(
                disabled
                  ? carePlansConstants.CONSENT_SECTION_DESCRIPTION_VIEW
                  : carePlansConstants.CONSENT_SECTION_DESCRIPTION
              )}{' '}
              <StyledLink to={`/residents/${residentId}/consent`}>
                {t(carePlansConstants.CONSENT_MANAGEMENT_LINK)}
              </StyledLink>
            </DescriptionText>
          </TitleContainer>
        }
        rightChildren={
          !disabled && (
            <Button
              type="button"
              variant="text"
              icon={<Add />}
              onClick={handleOpenModal}
            >
              {t(carePlansConstants.LINK_CONSENT_BUTTON)}
            </Button>
          )
        }
      >
        {hasConsentForms ? (
          <ConsentFormsList
            items={displayedForms}
            residentId={residentId}
            testId="consent-forms-list"
          />
        ) : undefined}
      </FormCard>
      <LinkConsentModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        residentId={residentId}
        linkedConsentIds={value}
        onConfirm={handleLinkConsents}
      />
    </>
  )
}

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const DescriptionText = styled(Text)`
  color: ${theme.color.neutral.color4};
`

const StyledLink = styled(Link)`
  color: ${theme.color.status.primary};
  text-decoration: underline;

  &:hover {
    color: ${theme.color.status.primary};
    text-decoration: underline;
  }
`

const FormCardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

export default ConsentSection
